import React, {useEffect} from 'react';
import {createRoot} from 'react-dom/client';
import {createRouter, RouterProvider} from '@tanstack/react-router';
import qs from 'qs';
import Cookies from 'lib/cookies';
import {jwtDecode as _decode} from 'lib/jwtDecode';
import {once} from 'lodash';
import track, {useTracking} from 'react-tracking';

/**
 * This is unused by this file but it is included here to resolve circular dependencies in child modules (inclusion of models directly).
 * THE ORDER OF THESE IMPORTS IS INTENTIONAL any file that could import a generated or augmented model should be
 * imported below this group.
 */
import 'resources/modelRegistry';

import notifier from '@albert-io/notifier';
import {resumeSession} from '@albert-io/mandark/authentication';

import {local} from 'lib/StorageUtil';
import {initStaleAppReload} from 'lib/staleAppReload';
import {routerHistory, browserHistory} from 'lib/router';
import sessionStore from 'client/Session/SessionStore';

import constants from 'client/constants';
import {getActiveState, callAction} from './framework';
import {appActions} from './AppActions';
import {routeTree} from './routeTree';

/**
 * We only inject getActiveState for development environments
 */
if (process.env.NODE_ENV === 'development') {
  global.getActiveState = getActiveState;
}

/**
 * significant part of this code is copied from
 * https://github.com/rafrex/react-router-hash-link-scroll
 */
function hashLinkScroll() {
  const {hash} = document.location;

  if (hash !== '') {
    // Push onto callback queue so it runs after the DOM is updated,
    // this is required when navigating from a different page so that
    // the element is rendered on the page before trying to getElementById.
    setTimeout(() => {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        /**
         * @todo add calculations for tertiary nav height if we want it fixed
         */
        // either global nav or marketing header render depending on if user is logged in or not.
        const navEl = document.querySelector('.global-nav, .marketing-header');
        const subheaderEl = document.querySelector('.subheader');
        const navElHeight = navEl ? navEl.getBoundingClientRect().height : 0;
        const subheaderElHeight = subheaderEl ? subheaderEl.getBoundingClientRect().height : 0;
        const FIXED_HEADER_HEIGHT = navElHeight + subheaderElHeight;
        const elementRectTop = Math.ceil(element.getBoundingClientRect().top);

        window.scrollTo(0, window.scrollY + elementRectTop - FIXED_HEADER_HEIGHT);
      } else {
        window.scrollTo(0, 0);
      }
    }, 0);
  } else {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }
}

const loginAtKey = 'albert.lms-login-at';

const trackLoginFromLms = once((callback) => {
  const launchContextCookie = Cookies.get('lti_launch_context');

  const loginAt = sessionStore.getLastAuthenticatedAt();
  const lastLoginAt = local.get(loginAtKey, '');
  const userIsResumingSession = typeof loginAt === 'string' && loginAt === lastLoginAt;
  if (launchContextCookie && !userIsResumingSession && loginAt) {
    local.set(loginAtKey, loginAt);
    const launchContextDecoded = _decode(launchContextCookie);
    const lmsPlatformName = launchContextDecoded.tool_platform_product_family_code || 'LMS';
    callback(lmsPlatformName);
  }
});

// Assigned just below; declared here so updateGlobalRouterProps (and the
// history subscription registered before createRouter) can close over it.
let router;

/**
 * Build the RR3-shaped `routerProps` (location/params/routes) consumed by
 * AppStore (and e.g. ConfirmLeaveModal). Mirrors what RR3's `onEnter`/`onChange`
 * used to push into the store.
 */
// Seed with the same safe location shape AppStore uses, so the first
// updateGlobalRouterProps() (before any navigation) dispatches a `prevRouterProps`
// that still has a `location` — otherwise it would overwrite AppStore's seed with
// a location-less `{}` and a consumer reading `prevRouterProps.location.*` on the
// initial paint would throw.
let prevRouterProps = {
  location: {pathname: '', search: '', query: {}, hash: '', state: null},
  params: {},
  routes: []
};
function updateGlobalRouterProps() {
  if (!router) {
    return;
  }
  // Read the LIVE location from history (updated synchronously on push/replace)
  // so `appStore.routerProps.location.query` is current the instant a navigation
  // happens — many features (e.g. the Dennis queue filters) read query params
  // from the store and would otherwise see stale data mid-transition.
  const location = browserHistory.getCurrentLocation();
  // Same-URL history events are no-ops: re-dispatching mid-transition is the
  // #18836 loop's feedback edge. state/key change per push (TanStack-internal)
  // and no store consumer reads them, so they are deliberately not compared.
  const prevLocation = prevRouterProps.location;
  if (
    location.pathname === prevLocation.pathname &&
    location.search === prevLocation.search &&
    location.hash === prevLocation.hash
  ) {
    return;
  }
  // Match params/routes from the LIVE pathname synchronously too. `router.state`
  // updates only after the route resolves (async), so on a SPA navigation it
  // still holds the PREVIOUS route's params for an instant — long enough for a
  // newly-mounted component (e.g. StudentDetail via studentDetailUtil) to read a
  // stale `params.studentId`. `getMatchedRoutes` resolves the current URL now.
  const {matchedRoutes = [], routeParams = {}} = router.getMatchedRoutes(location.pathname) ?? {};
  const routerProps = {
    location,
    params: routeParams,
    routes: matchedRoutes.map((route) => ({path: route.path, ...(route.options?.staticData ?? {})}))
  };
  callAction(appActions.SET_ROUTER_PROPS, {routerProps, prevRouterProps});
  const zone = routerProps.location.pathname.startsWith('/dennis')
    ? constants.ZONES.DENNIS
    : constants.ZONES.ALBERT;
  callAction(appActions.SET_ZONE, zone);
  prevRouterProps = routerProps;
}

// Subscribe to history BEFORE creating the router so this handler runs AHEAD of
// the router's own history subscriber. A synchronously-resolving route (no async
// beforeLoad/loader, e.g. StudentDetail) is rendered by the router from within
// its history-change callback; if our update ran after the router's, the
// just-mounted component would read the PREVIOUS route's `appStore.routerProps.
// params` (e.g. a stale studentId/subjectId → "This subject could not be found"
// on SPA navigation). Registering first guarantees params are fresh before the
// route renders. The `if (!router)` guard in updateGlobalRouterProps covers the
// gap until the assignment below (the subscription only fires on later changes).
routerHistory.subscribe(updateGlobalRouterProps);

router = createRouter({
  routeTree,
  history: routerHistory,
  // Parse/stringify search with qs so `location.query` (and navigate({search}))
  // round-trip exactly as they did under RR3.
  parseSearch: (search) => qs.parse(search.replace(/^\?/, '')),
  stringifySearch: (record) => {
    const str = qs.stringify(record);
    return str ? `?${str}` : '';
  },
  // RR3 matching was trailing-slash- and case-insensitive.
  trailingSlash: 'preserve',
  caseSensitive: false
});

initStaleAppReload(routerHistory);

/**
 * On every history change (the RR3 `<Router onUpdate>` equivalent): scroll
 * handling + GTM/LMS tracking. The global router-props refresh runs from a
 * separate, earlier history subscription (registered above, before the router)
 * so the store is fresh before any route renders. `useTracking()` is available
 * because this component is wrapped by `track(...)`.
 */
function RouterRoot() {
  const {trackEvent} = useTracking();

  useEffect(() => {
    const onHistoryChange = () => {
      getActiveState().forceUpdate().then(hashLinkScroll);
      // Function only runs once per runtime; catches/avoids tracking refreshes
      // and resumed sessions.
      trackLoginFromLms((method) => trackEvent({event: 'login', method}));
      trackEvent({event: 'gtm.historyChange'});
    };
    return routerHistory.subscribe(onHistoryChange);
  }, [trackEvent]);

  return <RouterProvider router={router} />;
}

const TrackedRouterRoot = track(
  {},
  {
    dispatch: (data) => {
      const eventObject = {
        ...data,
        timestamp: Date.now(),
        url: window.location.pathname,
        account_type: sessionStore.getAccountType(),
        user_id: sessionStore.getUserId(),
        logged_in: sessionStore.hasValidSession(),
        sign_up_date: sessionStore.getSignUpDate()
        // license_status @see https://github.com/albert-io/project-management/issues/5747
      };
      if (sessionStore.isTeacher()) {
        eventObject.user_email = sessionStore.getUserEmail();
      }
      // dataLayer only exists when the GTM snippet runs (production, or an
      // ad-blocker-free env) — drop events rather than throw without it.
      window.dataLayer?.push({
        event: eventObject.event,
        eventProps: eventObject
      });
    }
  }
)(RouterRoot);

// loadableReady() is SSR-only (it waits for server-flushed chunk state); this
// app is a pure SPA, so boot directly.
(async () => {
  // Restore the session ONCE at boot. RR3 resumed it in the root route's `onEnter`,
  // which ran a single time on entering `/`; TanStack's `rootRoute.beforeLoad`
  // re-runs on every route match, so doing it there would `await resumeSession()`
  // on every navigation and stall SPA navs. Skip the OAuth-error return flow
  // (`?state=error`), and never let a failed resume block boot — report and continue.
  if (qs.parse(window.location.search.replace(/^\?/, '')).state !== 'error') {
    try {
      await resumeSession();
    } catch (error) {
      // Only the fallback POST /auth/session/reset can reject here
      // (resumeSession swallows everything else). crossDomain/timeout is the
      // user's network, not a bug (HB 132118422); reset 401s/5xx stay reported.
      if (!error?.crossDomain && !error?.timeout) {
        notifier.notify(error, {
          message: 'Failed to restore session at boot',
          tags: 'authentication',
          context: {status: error?.status}
        });
      }
    }
  }
  // Populate AppStore router props before the first render (RR3 did this in the
  // root route's synchronous onEnter), so components reading
  // `appStore.routerProps.location.*` have a real value on initial paint.
  updateGlobalRouterProps();
  const root = createRoot(document.getElementById('app'));
  root.render(<TrackedRouterRoot />);
})();
